import { MusicPlayer } from './music';
import { RECIPES, recipeDuration, renderRecipe, resolveCue, type CanonicalCue, type SoundCue, type SoundScope } from './synth';

export type { SoundCue, SoundScope } from './synth';

/**
 * The recorded sound effects (made with ElevenLabs Sound Effects for the whole lineup, trimmed and
 * loudness-matched; files in apps/web/public/sfx). Each is decoded into a Web Audio buffer after the first
 * tap; until it has loaded (or if it fails) the cue's synthesized recipe plays instead.
 */
export const SAMPLE_CUES: readonly CanonicalCue[] = [
  'tap', 'select', 'join', 'leave', 'ready', 'unready', 'allReady', 'countdown', 'go', 'submit', 'tick', 'tickFinal',
  'reveal', 'correct', 'wrong', 'roundWin', 'roundLose', 'gameWin', 'gameLose', 'rematch', 'invite', 'vote', 'shutter', 'error',
];
export const sampleUrl = (cue: CanonicalCue) => `/sfx/${cue}.mp3`;

/**
 * Central sound effects: recorded samples (see SAMPLE_CUES) with synthesized fallbacks (synth.ts); any cue
 * can still be replaced by a custom asset with `setAsset`. Browsers block audio until a
 * user gesture, so the context is created/resumed on the first tap or key press; until then calls are
 * silently dropped.
 *
 * Who plays what: a phone in a room with a shared TV plays only `personal` cues (its own taps, lock-ins
 * and results); the TV plays the `room` cues (joins, all ready, reveals, the winner's fanfare) once for
 * everyone. Without a TV each phone plays both. The same cue twice within 120 ms plays once.
 */
export class SoundManager {
  private ctx: AudioContext | null = null;
  private bus: AudioNode | null = null;
  private assets = new Map<SoundCue, HTMLAudioElement>();
  private buffers = new Map<CanonicalCue, AudioBuffer>();
  private loadingSamples = false;
  /** A game's own recorded sounds (see loadGameSounds): id → decoded buffer, and ids still to fetch. */
  private gameBuffers = new Map<string, AudioBuffer>();
  private gameUrls = new Map<string, string>();
  private gameFetched = new Set<string>();
  private last = new Map<string, number>();
  muted = false;
  volume = 0.7;
  /** This device is the TV / audience screen. */
  tv = false;
  /** A phone in a room whose TV is on and in the same room: room-level cues are left to the TV. */
  sharedTv = false;
  /** The host's room setting: sound effects for the whole party. */
  roomSound = true;
  /** TV background music (only ever enabled on the audience screen). */
  readonly music: MusicPlayer;

  constructor(private factory?: () => AudioContext) {
    this.music = new MusicPlayer({ context: () => this.ctx });
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  /** Call from a user gesture (first tap). */
  unlock() {
    if (typeof window === 'undefined' && !this.factory) return;
    try {
      if (!this.ctx) {
        this.ctx = this.factory
          ? this.factory()
          : new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
        const ctx = this.ctx;
        // One soft compressor evens out the cues (and stops stacked cues from clipping).
        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = -18;
        comp.knee.value = 12;
        comp.ratio.value = 3;
        comp.attack.value = 0.003;
        comp.release.value = 0.15;
        comp.connect(ctx.destination);
        this.bus = comp;
        ctx.onstatechange = () => this.music.sync();
        this.music.sync();
      }
      // iPhones mute Web Audio while the ringer switch is on silent, unless the page asks for "playback"
      // audio (Safari 17+), and come back from the background "interrupted" rather than "suspended".
      try {
        const session = (navigator as { audioSession?: { type: string } }).audioSession;
        if (session && session.type !== 'playback') session.type = 'playback';
      } catch {
        /* not supported */
      }
      if (this.ctx.state !== 'running') void this.ctx.resume().then(() => this.music.sync(), () => undefined);
      this.loadSamples();
    } catch {
      this.ctx = null;
    }
  }

  /** Fetches and decodes the recorded cues (once, in the background). */
  private loadSamples() {
    const ctx = this.ctx;
    if (this.loadingSamples || !ctx || typeof window === 'undefined' || typeof fetch !== 'function') return;
    this.loadingSamples = true;
    for (const cue of SAMPLE_CUES) {
      void fetch(sampleUrl(cue))
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((bytes) => ctx.decodeAudioData(bytes))
        .then((buf) => void this.buffers.set(cue, buf))
        .catch(() => undefined);
    }
    this.fetchGameSounds();
  }

  /**
   * A game's own recorded sounds (id → url, e.g. '/sfx/recess/tag.mp3'). Fetched and decoded once the audio
   * is unlocked; safe to call on every render. Play them with playGameSound.
   */
  loadGameSounds(urls: Record<string, string>) {
    for (const [id, url] of Object.entries(urls)) if (!this.gameUrls.has(id)) this.gameUrls.set(id, url);
    this.fetchGameSounds();
  }

  private fetchGameSounds() {
    const ctx = this.ctx;
    if (!ctx || typeof window === 'undefined' || typeof fetch !== 'function') return;
    for (const [id, url] of this.gameUrls) {
      if (this.gameFetched.has(id)) continue;
      this.gameFetched.add(id);
      void fetch(url)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((bytes) => ctx.decodeAudioData(bytes))
        .then((buf) => void this.gameBuffers.set(id, buf))
        .catch(() => undefined);
    }
  }

  /**
   * Plays one of the game's recorded sounds. `room` sounds play once on the TV when there is one (phones leave
   * them to it); `personal` ones on this device. Returns false when it didn't play (muted, left to the TV,
   * audio locked, or not loaded yet), so a caller can fall back to a platform cue.
   */
  playGameSound(id: string, opts: { scope?: SoundScope; level?: number; delay?: number; duck?: number } = {}): boolean {
    if (this.muted || this.volume <= 0 || !this.roomSound) return false;
    if ((opts.scope ?? 'room') === 'room' && this.sharedTv && !this.tv) return false;
    const ctx = this.ctx;
    const buf = this.gameBuffers.get(id);
    if (!ctx || ctx.state !== 'running' || !this.bus || !buf) return false;
    const now = Date.now() + (opts.delay ?? 0) * 1000;
    const key = `game:${id}`;
    const prev = this.last.get(key);
    if (prev !== undefined && Math.abs(now - prev) < 90) return false;
    this.last.set(key, now);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = this.volume * (opts.level ?? 0.8);
    src.connect(g).connect(this.bus);
    src.start(ctx.currentTime + 0.01 + (opts.delay ?? 0));
    if (opts.duck) this.music.duck(opts.duck, buf.duration + 0.15);
    return true;
  }

  /** Whether the recorded version of a cue is ready (otherwise its synthesized recipe plays). */
  hasSample(cue: SoundCue): boolean {
    return this.buffers.has(resolveCue(cue));
  }

  setAsset(cue: SoundCue, url: string) {
    const a = new Audio(url);
    a.preload = 'auto';
    this.assets.set(cue, a);
  }

  /** Whether `play(cue)` would make a sound on this device right now (ignores the audio unlock). */
  audible(cue: SoundCue, scope?: SoundScope): boolean {
    if (this.muted || this.volume <= 0 || !this.roomSound) return false;
    const s = scope ?? RECIPES[resolveCue(cue)].scope;
    return !(s === 'room' && this.sharedTv && !this.tv);
  }

  /**
   * Plays a cue. `scope` overrides the cue's default (e.g. someone else's "ready" is a room cue, your own
   * is personal).
   */
  play(cue: SoundCue, opts: { scope?: SoundScope; delay?: number } = {}): void {
    this.playCue(cue, opts);
  }

  /** `play`, returning whether it made a sound. */
  playCue(cue: SoundCue, opts: { scope?: SoundScope; delay?: number } = {}): boolean {
    if (!this.audible(cue, opts.scope)) return false;
    const name = resolveCue(cue);
    const now = Date.now() + (opts.delay ?? 0) * 1000;
    const prev = this.last.get(name);
    if (prev !== undefined && Math.abs(now - prev) < 120) return false;
    const recipe = RECIPES[name];
    const asset = this.assets.get(cue) ?? this.assets.get(name);
    if (asset) {
      this.last.set(name, now);
      const a = asset.cloneNode() as HTMLAudioElement;
      a.volume = this.volume;
      void a.play().catch(() => undefined);
      this.music.duck(recipe.duck, 0.6);
      return true;
    }
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running' || !this.bus) return false;
    this.last.set(name, now);
    const at = ctx.currentTime + 0.01 + (opts.delay ?? 0);
    const buf = this.buffers.get(name);
    let length = recipeDuration(recipe);
    if (buf) {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const g = ctx.createGain();
      g.gain.value = this.volume * recipe.level;
      src.connect(g).connect(this.bus);
      src.start(at);
      length = buf.duration;
    } else {
      renderRecipe(ctx, this.bus, recipe, at, this.volume);
    }
    if (recipe.duck > 0) {
      if (opts.delay) setTimeout(() => this.music.duck(recipe.duck, length + 0.15), opts.delay * 1000);
      else this.music.duck(recipe.duck, length + 0.15);
    }
    return true;
  }

  /** Three countdown blips and a "go", on the audio clock. */
  countdown(opts: { scope?: SoundScope } = {}) {
    for (let i = 0; i < 3; i++) this.play('countdown', { ...opts, delay: i * 0.42 });
    this.play('go', { ...opts, delay: 1.26 });
  }

  /** Short haptic pulse where supported (Android). iOS Safari ignores it. */
  buzz(ms = 15) {
    try {
      navigator.vibrate?.(ms);
    } catch {
      /* ignore */
    }
  }
}
